import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList
} from 'recharts';
import { formatNumber } from '../services/format';

export type CartFlowChartPoint = {
  label: string;
  value: number;
  fill: string;
};

type TriangleBarProps = {
  fill?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  isActive?: boolean;
  payload?: CartFlowChartPoint;
};

type ValueLabelProps = {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  value?: number | string;
  payload?: CartFlowChartPoint;
};

function getTrianglePath(x: number, y: number, width: number, height: number) {
  return `M${x},${y + height}C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3}
  ${x + width / 2}, ${y}
  C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width}, ${y + height}
  Z`;
}

function TriangleBar(props: TriangleBarProps) {
  const { fill, x = 0, y = 0, width = 0, height = 0, isActive = false, payload } = props;
  const color = payload?.fill ?? fill ?? '#6A2ED3';

  if (width <= 0 || height <= 0) return null;

  return (
    <path
      d={getTrianglePath(x, y, width, height)}
      fill={color}
      stroke={color}
      strokeWidth={isActive ? 4 : 0}
      style={{ transition: 'stroke-width 0.3s ease-out' }}
    />
  );
}

function ValueLabel(props: ValueLabelProps) {
  const { x = 0, y = 0, width = 0, value, payload } = props;
  const numericX = Number(x);
  const numericY = Number(y);
  const numericWidth = Number(width);
  const color = payload?.fill ?? '#3A3A3A';

  if (!Number.isFinite(numericX) || !Number.isFinite(numericY)) return null;

  return (
    <text
      dominantBaseline="auto"
      fill={color}
      fontSize={12}
      fontWeight={600}
      textAnchor="middle"
      x={numericX + numericWidth / 2}
      y={numericY - 8}
    >
      {formatNumber(Number(value))}
    </text>
  );
}

export function CartFlowChart({ data }: { data: CartFlowChartPoint[] }) {
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#E0E0E0" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: '#8D8D8D', fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fill: '#8D8D8D', fontSize: 12 }} width={40} />
          <Tooltip
            cursor={{ fill: '#6A2ED3', fillOpacity: 0.08 }}
            formatter={(value) => formatNumber(Number(value))}
          />
          <Bar activeBar dataKey="value" shape={TriangleBar as never}>
            <LabelList content={ValueLabel as never} dataKey="value" position="top" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
